/**
 * /dashboard/hiring/candidates/$id — Candidate profile (Phase H.8).
 *
 * HR-admin OR an interviewer assigned to one of the candidate's
 * applications (server-resolved). A forbidden/unknown id is a NEUTRAL
 * 404 (no existence oracle).
 *
 * K1.7: the application context now comes from the real
 * GET /hiring/candidates/{id}/applications list (H.8 closeout); the
 * ?application=<id> search param from the pipeline board stays as the
 * preferred-selection fallback.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/candidates/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - useSearchParams (next) → Route.useSearch()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { z } from "zod";
import {
  ArrowLeft,
  Download,
  FileText,
  GitBranch,
  Link2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useCandidate,
  useCandidateApplications,
} from "@/hooks/use-hiring";
import { toApiError, hiringApi } from "@/lib/api";
import { useDirectory } from "@/hooks/use-directory";
import {
  APPLICATION_STATUS_LABELS,
  applicationTone,
  CANDIDATE_SOURCE_LABELS,
  fmtDate,
} from "@/lib/hiring-format";
import { candidateConsentBadge } from "@/lib/hiring-recruiter";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import {
  PHONE_BACK_LINK_CLASS,
  PhoneBackRow,
} from "@/components/hiring/phone-back-row";
import { OfferPanel } from "@/components/hiring/offer-panel";
import { CandidateComms } from "@/components/hiring/candidate-comms";
import { ScreeningAnswersPanel } from "@/components/hiring/screening-answers-panel";
import { isHiringHrAdmin } from "@/components/hiring/hr-admin-gate";
import { useAuth } from "@/lib/auth";
import { useInviteDelivery } from "@/hooks/use-employee-access";
import { HireDialog } from "@/components/hiring/hire-dialog";
import { ScheduleInterviewDialog } from "@/components/hiring/schedule-interview-dialog";
import { ProposeSlotsDialog } from "@/components/hiring/propose-slots-dialog";
import { InterviewsFeedbackPanel } from "@/components/hiring/interviews-feedback-panel";
import {
  CandidateHRDetails,
  CandidateMoreMenu,
} from "@/components/hiring/candidate-hr-details";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const candidateSearchSchema = z.object({
  // ApplicationCard passes the numeric application id from the pipeline
  // board. We accept either number or numeric string for robustness.
  application: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/candidates/$id",
)({
  validateSearch: candidateSearchSchema,
  component: CandidateProfileRoute,
});

function ResumeButton({ candidateId }: { candidateId: number }) {
  const [busy, setBusy] = useState(false);
  async function onView() {
    setBusy(true);
    try {
      const blob = await hiringApi.downloadResume(candidateId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      variant="outline"
      onClick={onView}
      disabled={busy}
      className="gap-1.5"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      View Résumé
    </Button>
  );
}

/**
 * K2.3 — "Copy status link" button (HR-admin or owning manager only).
 * POSTs to /hiring/applications/{id}/status-link, gets back the full public
 * URL, and copies it to the clipboard. The token is embedded in the URL;
 * the server reuses the live link (the candidate may already have it from
 * the application-received email), so copying never breaks it.
 */
function CopyStatusLinkButton({ applicationId }: { applicationId: number }) {
  const [busy, setBusy] = useState(false);

  async function onCopy() {
    setBusy(true);
    try {
      const { status_url } = await hiringApi.mintApplicationStatusLink(applicationId);
      if (status_url) {
        await navigator.clipboard.writeText(status_url);
        toast.success("Status link copied to clipboard");
      } else {
        toast.error("Could not build the status link");
      }
    } catch (e) {
      toast.error(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onCopy}
      disabled={busy}
      className="gap-1.5"
      title="Copy candidate status link"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Link2 className="h-3.5 w-3.5" />
      )}
      Copy Status Link
    </Button>
  );
}

function CandidateProfile({
  id,
  applicationId: preferredApplicationId,
}: {
  id: number;
  applicationId: number | null;
}) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const candQ = useCandidate(id);
  const dir = useDirectory();
  // K1.7: the real per-candidate application list is the primary
  // source; the ?application search param (pipeline-board context)
  // selects within it, and stays the fallback while the list loads or
  // when the caller (an interviewer) can see the candidate but the
  // list errs. Default = the newest application.
  const appsQ = useCandidateApplications(id);
  const applications = appsQ.data?.applications ?? [];
  const applicationId =
    preferredApplicationId != null &&
    (applications.length === 0 ||
      applications.some((a) => a.id === preferredApplicationId))
      ? preferredApplicationId
      : applications[0]?.id ?? null;
  // K2.4 — the current application's screening knockout flag (drives the
  // "Not shortlisted" note on the answers panel).
  const currentKnockoutFailed =
    applications.find((a) => a.id === applicationId)?.knockout_failed ?? false;
  const { user } = useAuth();
  const isAdmin = isHiringHrAdmin(user?.role);
  // Whether the new hire's invite is actually emailed (no SMTP: it isn't).
  const inviteDelivery = useInviteDelivery(isAdmin);

  if (candQ.isLoading) {
    return (
      <HiringModuleShell title="Candidate">
        <Card>
          <CardContent className="space-y-4 py-8">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  if (candQ.isError) {
    const e = toApiError(candQ.error);
    return (
      <HiringModuleShell title="Candidate">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {e.error === "not_found" || e.error === "forbidden"
                ? "This candidate isn't available. You need to be HR, the hiring manager for their job, or one of their interviewers."
                : e.message}
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/hiring/candidates" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to Candidates
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  const c = candQ.data!.candidate;
  const fullName =
    `${c.first_name} ${c.last_name}`.trim() || c.email;

  return (
    <>
    <PhoneBackRow>
      {isAdmin ? (
        <Link
          to="/app/$tenantSlug/hiring/candidates"
          params={{ tenantSlug }}
          className={PHONE_BACK_LINK_CLASS}
        >
          <ArrowLeft className="h-4 w-4" />
          All Candidates
        </Link>
      ) : (
        <Link
          to="/app/$tenantSlug/interviews"
          params={{ tenantSlug }}
          className={PHONE_BACK_LINK_CLASS}
        >
          <ArrowLeft className="h-4 w-4" />
          My Interviews
        </Link>
      )}
    </PhoneBackRow>
    <HiringModuleShell
      title={fullName}
      description={c.email}
      actions={
        // Phones: [Schedule Interview][⋯] then a full-width Propose
        // Interview Times; the Back link sits above the title and Erase
        // Candidate is in the ⋯ menu. sm+: one wrapping row, ⋯ last.
        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:w-auto sm:flex-wrap">
          {applicationId != null && (
            <div className="min-w-0 sm:contents [&>button]:w-full sm:[&>button]:w-auto">
              <ScheduleInterviewDialog applicationId={applicationId} />
            </div>
          )}
          <CandidateMoreMenu
            c={c}
            hired={applications.some((a) => a.status === "hired")}
            className="col-start-2 row-start-1 sm:order-last"
          />
          {applicationId != null && isAdmin && (
            <div className="col-span-2 min-w-0 sm:contents [&>button]:w-full sm:[&>button]:w-auto">
              <ProposeSlotsDialog applicationId={applicationId} />
            </div>
          )}
          {!isAdmin && (
            <Button asChild variant="ghost" className="hidden gap-1.5 lg:inline-flex">
              <Link to="/app/$tenantSlug/interviews" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                My Interviews
              </Link>
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="font-medium">{c.email}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="font-medium">
                  {c.phone ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Source</dt>
                <dd>
                  <StatusBadge
                    label={CANDIDATE_SOURCE_LABELS[c.source]}
                    tone="info"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  Consent
                </dt>
                <dd>
                  <StatusBadge {...candidateConsentBadge(c)} />
                </dd>
              </div>
              {c.source_url && (
                <div className="sm:col-span-2">
                  <dt className="text-muted-foreground">
                    Source Link
                  </dt>
                  <dd className="break-all font-medium">
                    <a
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-primary hover:underline"
                    >
                      {c.source_url}
                    </a>
                  </dd>
                </div>
              )}
              {c.retention_until && (
                <div>
                  <dt className="text-muted-foreground">
                    Retention Until
                  </dt>
                  <dd className="font-medium">
                    {fmtDate(c.retention_until)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-muted-foreground">Added</dt>
                <dd className="font-medium">
                  {fmtDate(c.created_at)}
                </dd>
              </div>
              {c.imported_by_id != null && (
                <div>
                  <dt className="text-muted-foreground">
                    Imported By
                  </dt>
                  <dd className="font-medium">
                    {dir.label(c.imported_by_id)}
                  </dd>
                </div>
              )}
              {c.referred_by_id != null && (
                <div>
                  <dt className="text-muted-foreground">
                    Referred By
                  </dt>
                  <dd className="font-medium">
                    {dir.label(c.referred_by_id)}
                  </dd>
                </div>
              )}
            </dl>

            {/* HR-only: notes + current / expected pay + notice period,
                editable. The server leaves these out for interviewers
                and hiring managers (hr_view=false → renders nothing). */}
            <CandidateHRDetails c={c} />

            <div className="flex items-center gap-3 border-t pt-4">
              {c.has_resume ? (
                <ResumeButton candidateId={c.id} />
              ) : (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  No résumé on file.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hire</CardTitle>
            <CardDescription>
              Turn this candidate into an employee and start their
              onboarding.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {applicationId != null ? (
              <>
                <HireDialog
                  applicationId={applicationId}
                  candidate={c}
                />
                <p className="text-xs text-muted-foreground">
                  Only an active application can be hired.{" "}
                  {inviteDelivery.data?.email_configured === false
                    ? "Email isn't set up, so no invite is emailed — you get a one-time temporary password to share with the new hire privately."
                    : "The new hire is emailed an invite to set their password, and you also get a one-time temporary password as a fallback."}
                </p>
              </>
            ) : (
              <Alert>
                <AlertTitle>Open from the pipeline</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    To hire, schedule an interview or make an offer,
                    open this candidate from a requisition&apos;s
                    pipeline board (that&apos;s where their application
                    lives).
                  </p>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                  >
                    <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
                      <GitBranch className="h-4 w-4" />
                      Go to Requisitions
                    </Link>
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {applications.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Applications</CardTitle>
            <CardDescription>
              Every requisition this candidate has applied to. Select
              one to act on it (interviews, offer, hire).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {applications.map((a) => (
              <div
                key={a.id}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 ${
                  a.id === applicationId ? "border-primary/60 bg-primary/5" : ""
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {a.requisition_title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {a.stage_name} · applied {fmtDate(a.applied_at)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    label={APPLICATION_STATUS_LABELS[a.status]}
                    tone={applicationTone(a.status)}
                  />
                  {/* K2.3 — HR-admin/manager can mint a tokenized public
                      status link and copy it to share with the candidate. */}
                  {isAdmin && (
                    <CopyStatusLinkButton applicationId={a.id} />
                  )}
                  {a.id === applicationId ? (
                    <span className="text-xs font-medium text-primary">
                      Selected
                    </span>
                  ) : (
                    <Button asChild size="sm" variant="outline">
                      <Link
                        to="/app/$tenantSlug/hiring/candidates/$id"
                        params={{ tenantSlug, id: String(id) }}
                        search={{ application: a.id }}
                      >
                        Select
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Wave-B area 2 — rounds, interviewers and their scorecards (HR
          and the hiring manager; hides itself for anyone else). */}
      {applicationId != null && (
        <div className="mt-6">
          <InterviewsFeedbackPanel applicationId={applicationId} />
        </div>
      )}

      {applicationId != null && (
        <div className="mt-6">
          <OfferPanel applicationId={applicationId} />
        </div>
      )}

      {/* K2.4 — the applicant's screening answers (self-hides when the req
          has no screening questions or the caller has no read). */}
      {applicationId != null && (
        <div className="mt-6">
          <ScreeningAnswersPanel
            applicationId={applicationId}
            knockoutFailed={currentKnockoutFailed}
          />
        </div>
      )}

      {/* K1.3 — the communication timeline + "Email candidate". The
          section self-hides on the server's neutral 404 (a caller with
          no read relationship, e.g. an interviewer). */}
      <div className="mt-6">
        <CandidateComms
          candidateId={c.id}
          applicationId={applicationId}
          consentGiven={c.consent_given}
          canSend={isAdmin}
        />
      </div>
    </HiringModuleShell>
    </>
  );
}

function CandidateProfileRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const numId = Number(id);
  const applicationId = search.application ?? null;

  if (Number.isFinite(numId) && numId > 0) {
    return (
      <CandidateProfile
        id={numId}
        applicationId={applicationId}
      />
    );
  }
  return (
    <HiringModuleShell title="Candidate">
      <Card>
        <CardContent className="py-16 text-center">
          <p className="text-sm text-muted-foreground">
            Invalid candidate.
          </p>
        </CardContent>
      </Card>
    </HiringModuleShell>
  );
}
